import { useEffect } from 'react'

const REVEAL_SELECTOR = '.reveal, .reveal-left, .reveal-right, .reveal-zoom, .reveal-flip, .reveal-stagger'

export const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches

// Scroll reveals play once, then the element is unobserved (the prototype
// replayed them in both directions, which kept the page busy while scrolling).
const setupReveal = (root) => {
    const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (entry.isIntersecting) {
                entry.target.classList.add('in-view')
                observer.unobserve(entry.target)
            }
        })
    }, { threshold: 0.12, rootMargin: '0px 0px -40px 0px' })
    root.querySelectorAll(REVEAL_SELECTOR).forEach((el) => observer.observe(el))
    return () => observer.disconnect()
}

// Looping CSS animations inside [data-anim] pause while that block is off screen.
const setupAnimationPausing = (root) => {
    const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => entry.target.classList.toggle('anim-paused', !entry.isIntersecting))
    })
    root.querySelectorAll('[data-anim]').forEach((el) => observer.observe(el))
    return () => observer.disconnect()
}

export const animateCounter = (el) => {
    const target = parseFloat(el.dataset.target)
    const suffix = el.dataset.suffix || ''
    const duration = 1400
    const start = performance.now()
    const tick = (now) => {
        const p = Math.min((now - start) / duration, 1)
        const eased = 1 - Math.pow(1 - p, 3)
        el.textContent = Math.round(target * eased).toLocaleString('en-IN') + suffix
        if (p < 1) requestAnimationFrame(tick)
    }
    requestAnimationFrame(tick)
}

const setupCounters = (root) => {
    const counters = root.querySelectorAll('.counter[data-target]')
    if (prefersReducedMotion()) {
        counters.forEach((el) => {
            el.textContent = parseFloat(el.dataset.target).toLocaleString('en-IN') + (el.dataset.suffix || '')
        })
        return () => {}
    }
    const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (entry.isIntersecting) {
                animateCounter(entry.target)
                observer.unobserve(entry.target)
            }
        })
    }, { threshold: 0.5 })
    counters.forEach((el) => observer.observe(el))
    return () => observer.disconnect()
}

// 3D tilt + glare on hover. Mouse moves are coalesced to one update per frame,
// and the card rect is measured once on enter rather than on every move.
const setupTilt = (root) => {
    const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)').matches
    if (!finePointer || prefersReducedMotion()) return () => {}

    const cleanups = []
    root.querySelectorAll('.tilt').forEach((card) => {
        const glare = document.createElement('div')
        glare.className = 'glare'
        card.appendChild(glare)

        let rect = null
        let frame = 0
        let pointer = { x: 0, y: 0 }

        const apply = () => {
            frame = 0
            const px = (pointer.x - rect.left) / rect.width
            const py = (pointer.y - rect.top) / rect.height
            const rx = (py - 0.5) * -10
            const ry = (px - 0.5) * 12
            card.style.setProperty('--gx', `${px * 100}%`)
            card.style.setProperty('--gy', `${py * 100}%`)
            card.style.transform = `perspective(900px) rotateX(${rx}deg) rotateY(${ry}deg) translateY(-6px) scale(1.02)`
        }
        const onEnter = () => {
            rect = card.getBoundingClientRect()
            card.style.transition = 'transform .08s ease, box-shadow .3s ease, border-color .3s ease'
        }
        const onMove = (e) => {
            if (!rect) rect = card.getBoundingClientRect()
            pointer = { x: e.clientX, y: e.clientY }
            if (!frame) frame = requestAnimationFrame(apply)
        }
        const onLeave = () => {
            if (frame) cancelAnimationFrame(frame)
            frame = 0
            rect = null
            card.style.transition = 'transform .5s cubic-bezier(.2,.7,.2,1), box-shadow .3s ease, border-color .3s ease'
            card.style.transform = ''
        }

        card.addEventListener('mouseenter', onEnter)
        card.addEventListener('mousemove', onMove)
        card.addEventListener('mouseleave', onLeave)
        cleanups.push(() => {
            card.removeEventListener('mouseenter', onEnter)
            card.removeEventListener('mousemove', onMove)
            card.removeEventListener('mouseleave', onLeave)
            if (frame) cancelAnimationFrame(frame)
            glare.remove()
            card.style.transform = ''
        })
    })
    return () => cleanups.forEach((fn) => fn())
}

const useLandingEffects = (rootRef) => {
    useEffect(() => {
        const root = rootRef.current
        if (!root) return undefined
        const cleanups = [
            setupReveal(root),
            setupAnimationPausing(root),
            setupCounters(root),
            setupTilt(root),
        ]
        return () => cleanups.forEach((fn) => fn())
    }, [rootRef])
}

export default useLandingEffects
